import {
  Inject,
  Injectable,
  makeStateKey,
  Optional,
  Renderer2,
  RESPONSE_INIT,
  StateKey,
  TransferState
} from '@angular/core';
import {PointOfInterestDTO} from '../dto/point-of-interest-dto';
import {PointOfInterest} from '../classes/point-of-interest';
import {HttpClient} from '@angular/common/http';
import {StorageService} from './storage.service';
import {NavigationService} from './navigation.service';
import {ToastService} from './toast.service';
import {StorableService} from './storable.service';
import {BehaviorSubject, map} from 'rxjs';
import {GroupedByCategory} from '../interfaces/grouped-by-category';
import {GroupedPointOfInterestMarker} from '../interfaces/grouped-point-of-interest-marker';
import {kebabCase} from 'lodash';
import {Category} from '../classes/category';
import {PlatformService} from './platform.service';
import {CategoryService} from './category.service';
import {StoryService} from './story.service';
import {Story} from '../classes/story';
import {Role} from '../enums/role';

@Injectable({
  providedIn: 'root'
})
export class PointOfInterestService extends StorableService<PointOfInterestDTO, PointOfInterest> {
  private _groupedByCategory: GroupedByCategory<PointOfInterest|Story>[] = [];
  private _groupedByCategory$: BehaviorSubject<GroupedByCategory<PointOfInterest|Story>[]> = new BehaviorSubject<GroupedByCategory<PointOfInterest|Story>[]>([]);
  private _groupedByLatLong: GroupedPointOfInterestMarker[] = [];
  private _groupedByLatLong$: BehaviorSubject<GroupedPointOfInterestMarker[]> = new BehaviorSubject<GroupedPointOfInterestMarker[]>([]);
  private _filteredGroupedInterestMarkers: GroupedPointOfInterestMarker[] = [];
  private _filteredGroupedInterestMarkers$: BehaviorSubject<GroupedPointOfInterestMarker[]> = new BehaviorSubject<GroupedPointOfInterestMarker[]>([]);
  private _filteredGroupedByCategory: GroupedByCategory<PointOfInterest | Story>[] = [];
  private _filteredGroupedByCategory$: BehaviorSubject<GroupedByCategory<PointOfInterest | Story>[]> = new BehaviorSubject<GroupedByCategory<PointOfInterest | Story>[]>([]);
  // todo: add text search to this component
  private _updatedData$ = new BehaviorSubject<null>(null);
  private _searchBarItems$: BehaviorSubject<GroupedByCategory<PointOfInterest | Story>[]> = new BehaviorSubject<GroupedByCategory<PointOfInterest | Story>[]>([]);
  private _stories: Story[] = [];
  openedPointOfInterestMarker: GroupedPointOfInterestMarker | null = null;

  constructor(http: HttpClient, storageService: StorageService, transferState: TransferState,
              navigationService: NavigationService, toastService: ToastService, platformService: PlatformService,
              private _categoryService: CategoryService, private _storyService: StoryService, @Optional() @Inject(RESPONSE_INIT) response?: ResponseInit) {
    super(http, storageService, transferState, navigationService, toastService, platformService, response);
    this._storyService.getData().subscribe({
      next: () => {
        this._stories = this._storyService.getData().value;
        this.updateData();
      },
    });
  }

  readonly tableName = 'point_of_interests';

  readonly endpoint = '/point-of-interests';

  get stateKey(): StateKey<PointOfInterestDTO[]> {
    return makeStateKey("point_of_interests");
  }

  protected createData(dto: PointOfInterestDTO): PointOfInterest {
    return new PointOfInterest(dto);
  }

  override updateData() {
    super.updateData();
    this._updateDataWithCategories();
    this._updateGroupedByCategory();
    this._updateGroupedByLatLong();
    this._updateSearchBarItems();
    this._updatedData$.next(null);
  }

  private _updateGroupedByCategory() {
    const categoryData = this._categoryService.getData().value;
    const mergedPointsOfInterests = [...this.data.filter(pointOfInterest => !pointOfInterest.isThemeLocation), ...this._stories];
    const groupedByCategory: GroupedByCategory<PointOfInterest|Story>[] = mergedPointsOfInterests.flatMap(pointOfInterests => {
      const categoriesCopy: Category[] = [];
      categoryData.forEach(category => categoriesCopy.push(new Category(category.getDTO())));
      return pointOfInterests.categoryIds.map(id => categoriesCopy.find(category => category.id === id)).filter(item => !!item);
    })
      .filter((category, index, categories) => index === categories.findIndex(cat => (cat.id === category.id)))
      .map(category => {
        return {
          category: category,
          classes: mergedPointsOfInterests.filter(pointOfInterest => pointOfInterest.categoryIds.includes(category.id)),
        };
      });
    this._groupedByCategory = groupedByCategory;
    this._groupedByCategory$.next(groupedByCategory);
  }

  private _updateGroupedByLatLong() {
    const groupedByLatLong: GroupedPointOfInterestMarker[] = [];
    const mergedPointsOfInterests = [...this.data.filter(pointOfInterest => !pointOfInterest.isThemeLocation), ...this._stories];
    mergedPointsOfInterests.forEach((pointOfInterest, index: number) => {
      const itemInArray = groupedByLatLong.find(groupedItem => JSON.stringify(groupedItem.googleMapsLatLngLiteral) === JSON.stringify(pointOfInterest.googleMapsLatLngLiteral));
      if (!itemInArray) {
        groupedByLatLong.push({
          id: index++,
          googleMapsLatLngLiteral: pointOfInterest.googleMapsLatLngLiteral,
          pointOfInterests: [pointOfInterest]
        });
        return;
      }
      itemInArray.pointOfInterests.push(pointOfInterest);
    });
    this._groupedByLatLong = groupedByLatLong;
    this._groupedByLatLong$.next(groupedByLatLong);
    this._updateSearchBarItems();
  }

  getGroupedByCategory() {
    return this._groupedByCategory$;
  }

  getGroupedByLatLong() {
    return this._groupedByLatLong$;
  }

  getSearchBarItems() {
    return this._searchBarItems$;
  }

  getFilteredGroupedInterestMarkers() {
    return this._filteredGroupedInterestMarkers$;
  }

  getFilteredPointOfInterestsGroupedByCategory() {
    return this._filteredGroupedByCategory$.pipe(map((group) => {
      group.map(group => group.classes.sort((a, b) => {
        const firstValue = a instanceof PointOfInterest ? (a.role === Role.AMBASSADOR || a.role === Role.FOUNDER) : 0;
        const secondValue = b instanceof PointOfInterest ? (b.role === Role.AMBASSADOR || b.role === Role.FOUNDER) : 0;
        return Number(secondValue) - Number(firstValue);
      }));
      return group;
    }));
  }

  getUpdatedData() {
    return this._updatedData$;
  }

  filterGroupedByCategoryByCategoryIds(categoryIds: number[] | null) {
    const filtered: GroupedByCategory<PointOfInterest|Story>[] = [];
    if (categoryIds === null) {
      this._groupedByCategory.forEach(groupedByCategory => groupedByCategory.classes.sort((a, b) => a.title.localeCompare(b.title)));
      this._filteredGroupedByCategory = this._groupedByCategory;
      this._filteredGroupedByCategory$.next(this._groupedByCategory);
      this._updateSearchBarItems();
      return;
    }
    this._groupedByCategory.forEach(groupedItem => {
      const copy: GroupedByCategory<PointOfInterest|Story> = {category: groupedItem.category, classes: []};
      if (categoryIds.includes(groupedItem.category.id)) {
        groupedItem.classes.forEach(pointOfInterest => {
          copy.classes.push(pointOfInterest);
        });
        filtered.push(copy);
      }
    });

    filtered.forEach(groupedByCategory => groupedByCategory.classes.sort((a, b) => a.title.localeCompare(b.title)));
    this._filteredGroupedByCategory = filtered;
    this._filteredGroupedByCategory$.next(this._filteredGroupedByCategory);
    this._updateSearchBarItems();
  }

  filterGroupedByLatLongByCategoryIds(categoryIds: number[] | null): void {
    const filtered: GroupedPointOfInterestMarker[] = [];
    this._groupedByLatLong.forEach(groupedItem => {
      const groupedCopy: GroupedPointOfInterestMarker = {
        id: groupedItem.id,
        googleMapsLatLngLiteral: groupedItem.googleMapsLatLngLiteral,
        markerContent: groupedItem.markerContent,
        markerContentSelected: groupedItem.markerContentSelected,
        pointOfInterests: []
      };
      groupedCopy.pointOfInterests = [...groupedItem.pointOfInterests];
      if (categoryIds) {
        groupedCopy.pointOfInterests = groupedCopy.pointOfInterests.filter(pointOfInterest => pointOfInterest.categoryIds.some(item => categoryIds.includes(item)));
      }
      filtered.push(groupedCopy);
    });
    this._filteredGroupedInterestMarkers = filtered.filter(groupedItem => groupedItem.pointOfInterests.length > 0);
    this._filteredGroupedInterestMarkers$.next(this._filteredGroupedInterestMarkers);
  }

  addMarkers(renderer2: Renderer2) {
    // todo: Show correct marker when there are no longer two
    const groupedByLatLong = this._groupedByLatLong.map(groupedItem => {
      const element = renderer2.createElement('div');
      let markerName = 'default';
      const categoryId = groupedItem.pointOfInterests[0].mainCategoryId;

      if (groupedItem.pointOfInterests.length < 2 || groupedItem.pointOfInterests.every(poi => poi.mainCategoryId === categoryId)) {
        const category = this._categoryService.findById(categoryId);
        if (category) {
          markerName = category.name.toLowerCase();
          markerName = kebabCase(markerName);
        }
        element.innerHTML = `<img src="/svg/categories/markers/${markerName}.svg" alt="" class="w-8"/>`;
      }
      if (groupedItem.pointOfInterests.length > 1 && !groupedItem.pointOfInterests.every(poi => poi.mainCategoryId === categoryId)) {
        element.style.position = 'relative';
        element.innerHTML = `<span class="cluster-content">${groupedItem.pointOfInterests.length}</span><img src="/svg/categories/markers/${markerName}.svg" alt="" class="w-8"/>`;
      }

      groupedItem.markerContent = element;
      const defaultElement = renderer2.createElement('div');
      defaultElement.innerHTML = `<img src="/svg/categories/markers/selected.svg" alt="" class="w-12"/>`;
      groupedItem.markerContentSelected = defaultElement;

      return groupedItem;
    });
    this._groupedByLatLong$.next(groupedByLatLong);
    this._updateSearchBarItems();
  }

  private _updateDataWithCategories() {
    const pointOfInterestsWithCategories = this.data.map(pointOfInterest => {
      pointOfInterest.categories = pointOfInterest.categoryIds.map(id => this._categoryService.findById(id)).filter(category => !!category);
      return pointOfInterest;
    });
    this._stories = this._stories.map(story => {
      story.categories = story.categoryIds.map(id => this._categoryService.findById(id)).filter(category => !!category);
      return story;
    });
    this.data$.next(pointOfInterestsWithCategories);
  }

  private _updateSearchBarItems() {
    this._searchBarItems$.next(this._filteredGroupedByCategory.length > 0 ? this._filteredGroupedByCategory : this.getGroupedByCategory().value);
  }
}
